'use client';

import React, { createContext, useContext, useState, useRef } from 'react';
import { Episode, dataService } from '@/lib/dataService';

interface AudioPlayerContextType {
  currentEpisode: Episode | null;
  isPlaying: boolean;
  playEpisode: (episode: Episode) => void;
  pauseEpisode: () => void;
  resumeEpisode: () => void;
  togglePlay: () => void;
  volume: number;
  setVolume: (vol: number) => void;
  currentTime: number;
  duration: number;
  seekTo: (seconds: number) => void;
  isVideoModalOpen: boolean;
  setIsVideoModalOpen: (open: boolean) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const AudioPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentEpisode, setCurrentEpisode] = useState<Episode | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.8);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const playEpisode = (episode: Episode) => {
    setCurrentEpisode(episode);
    setIsPlaying(true);
    setDuration(episode.duration_seconds || 1800);
    setCurrentTime(0);

    // Record view in analytics
    dataService.recordPlay(episode.id, Math.floor((episode.duration_seconds || 1800) / 4));

    if (episode.media_type === 'video') {
      setIsVideoModalOpen(true);
    }
  };

  const pauseEpisode = () => {
    setIsPlaying(false);
  };

  const resumeEpisode = () => {
    if (currentEpisode) {
      setIsPlaying(true);
    }
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const setVolume = (vol: number) => {
    setVolumeState(vol);
  };

  const seekTo = (seconds: number) => {
    setCurrentTime(seconds);
  };

  return (
    <AudioPlayerContext.Provider
      value={{
        currentEpisode,
        isPlaying,
        playEpisode,
        pauseEpisode,
        resumeEpisode,
        togglePlay,
        volume,
        setVolume,
        currentTime,
        duration,
        seekTo,
        isVideoModalOpen,
        setIsVideoModalOpen,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};
